import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AuthModal from "../components/AuthModal";
import { getGuestCart, clearGuestCart } from "../utils/guestCart";
import { Plus } from "lucide-react";
import { toast } from "sonner";

// Key used to persist checkout intent across Google OAuth redirect
const CHECKOUT_INTENT_KEY = "rivora_checkout_intent";

export default function CheckoutPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  // ── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart]             = useState(null);
  const [guestItems, setGuestItems] = useState([]);

  // ── Page state ────────────────────────────────────────────────────────────
  const [addresses, setAddresses]         = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [loading, setLoading]             = useState(true);
  const [processing, setProcessing]       = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [couponCode, setCouponCode]       = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);

  // Inline address form — used by both guests (before sign-in) and logged-in users (add new)
  const emptyAddr = { full_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", is_default: false };
  const [newAddress, setNewAddress] = useState(emptyAddr);

  // ── Load on mount + handle Google OAuth return ────────────────────────────
  useEffect(() => {
    fetchAvailableCoupons();

    if (user) {
      // Check if we're returning from a Google OAuth redirect with a saved intent
      const intentRaw = localStorage.getItem(CHECKOUT_INTENT_KEY);
      if (intentRaw) {
        try {
          const intent = JSON.parse(intentRaw);
          localStorage.removeItem(CHECKOUT_INTENT_KEY);
          // Restore the address form data the user had filled before the redirect
          if (intent.address) setNewAddress(intent.address);
          // Small delay to ensure auth cookie is fully committed by mobile browsers
          // before firing authenticated API calls
          setTimeout(() => resumeAfterGoogleAuth(user, intent), 300);
          return;
        } catch { localStorage.removeItem(CHECKOUT_INTENT_KEY); }
      }
      loadLoggedInData();
    } else {
      // Check if there's an intent waiting — user state may not be set yet on
      // mobile (AuthCallback sets user then navigates, but context update can lag)
      const intentRaw = localStorage.getItem(CHECKOUT_INTENT_KEY);
      if (intentRaw) {
        // Intent exists but user not set yet — show loading, wait for user effect to re-run
        setLoading(true);
      } else {
        setGuestItems(getGuestCart());
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadLoggedInData = async () => {
    setLoading(true);
    try {
      const [cartRes, addrRes] = await Promise.all([
        axios.get(`${API}/cart`, { withCredentials: true }),
        axios.get(`${API}/addresses`, { withCredentials: true }),
      ]);
      setCart(cartRes.data);
      setAddresses(addrRes.data);
      const def = addrRes.data.find(a => a.is_default);
      setSelectedAddress(def ? def.address_id : addrRes.data[0]?.address_id || "");
    } catch {
      toast.error("Failed to load checkout data");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCoupons = async () => {
    try {
      const { data } = await axios.get(`${API}/coupons/public`);
      setAvailableCoupons(data);
    } catch { /* optional */ }
  };

  // ── Save checkout intent before Google redirect ───────────────────────────
  // Called by AuthModal just before navigating to Google OAuth
  const saveCheckoutIntent = () => {
    const intent = {
      address:    newAddress,
      guestCart:  getGuestCart(),
      couponCode: couponApplied ? couponCode : null,
    };
    localStorage.setItem(CHECKOUT_INTENT_KEY, JSON.stringify(intent));
  };

  // ── Resume checkout after returning from Google OAuth ─────────────────────
  const resumeAfterGoogleAuth = async (currentUser, intent) => {
    setLoading(true);

    // Retry helper — mobile Safari sometimes needs a moment for the auth cookie
    // to be available to subsequent requests after a redirect chain
    const withRetry = async (fn, attempts = 3, delayMs = 500) => {
      for (let i = 0; i < attempts; i++) {
        try {
          return await fn();
        } catch (err) {
          const status = err?.response?.status;
          // Only retry on 401/403 (auth not ready yet), not on other errors
          if ((status === 401 || status === 403) && i < attempts - 1) {
            await new Promise(r => setTimeout(r, delayMs * (i + 1)));
            continue;
          }
          throw err;
        }
      }
    };

    try {
      // 1. Merge guest cart
      const guestCart = intent.guestCart || [];
      if (guestCart.length > 0) {
        for (const item of guestCart) {
          await withRetry(() =>
            axios.post(
              `${API}/cart`,
              { product_id: item.product_id, quantity: item.quantity },
              { withCredentials: true }
            )
          );
        }
        clearGuestCart();
      }

      // 2. Save the address
      const savedAddrRes = await withRetry(() =>
        axios.post(`${API}/addresses`, intent.address, { withCredentials: true })
      );
      const savedAddr = savedAddrRes.data;

      // 3. Load cart
      const cartRes = await withRetry(() =>
        axios.get(`${API}/cart`, { withCredentials: true })
      );
      setCart(cartRes.data);
      setAddresses([savedAddr]);
      setSelectedAddress(savedAddr.address_id);

      // 4. Restore coupon if any
      if (intent.couponCode) {
        setCouponCode(intent.couponCode);
        try {
          const total = (cartRes.data?.items || []).reduce((s, i) => s + (i.product?.price || 0) * i.quantity, 0);
          const { data: cpn } = await axios.post(
            `${API}/coupons/validate`,
            { code: intent.couponCode, total },
            { params: { code: intent.couponCode, total } }
          );
          const discountAmount = Number(cpn.discount ?? (cpn.final_total ? total - cpn.final_total : 0));
          if (discountAmount > 0) {
            setCouponDiscount(discountAmount);
            setCouponApplied(true);
          }
        } catch { /* coupon may have expired — ignore */ }
      }

      setLoading(false);

      // 5. Open Razorpay
      await openRazorpay(savedAddr.address_id, currentUser);
    } catch (err) {
      toast.error("Could not resume checkout. Please try again.");
      setLoading(false);
    }
  };

  // ── Cart items (guest or real) ────────────────────────────────────────────
  const cartItems = user ? (cart?.items || []) : guestItems;
  const calculateTotal = () =>
    cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  // ── Validate the inline address form ─────────────────────────────────────
  const isAddressFormValid = () => {
    const { full_name, phone, address_line1, city, state, pincode } = newAddress;
    return full_name && phone && address_line1 && city && state && pincode;
  };

  // ── Save address to backend, return address_id ────────────────────────────
  const saveAddressToBackend = async () => {
    const { data } = await axios.post(`${API}/addresses`, newAddress, { withCredentials: true });
    setAddresses(prev => [...prev, data]);
    setSelectedAddress(data.address_id);
    setShowAddressForm(false);
    setNewAddress(emptyAddr);
    return data.address_id;
  };

  // ── Coupon ────────────────────────────────────────────────────────────────
  const applyCoupon = async () => {
    if (!couponCode) return;
    const total = calculateTotal();
    if (total === 0) {
      toast.error("Add items to your cart first");
      return;
    }
    try {
      // Send both query params and body to maximise compatibility across
      // middleware (express-mongo-sanitize can sometimes strip query params)
      const { data } = await axios.post(
        `${API}/coupons/validate`,
        { code: couponCode, total },           // body
        { params: { code: couponCode, total } } // query params as fallback
      );

      const discountAmount = Number(data.discount ?? (
        data.final_total != null ? total - Number(data.final_total) : 0
      ));

      if (!discountAmount || discountAmount <= 0) {
        toast.error("This coupon gives no discount on your current order.");
        return;
      }

      setCouponDiscount(discountAmount);
      setCouponApplied(true);
      toast.success(`Coupon applied! You save ₹${discountAmount.toLocaleString('en-IN')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid coupon");
      setCouponApplied(false); setCouponDiscount(0);
    }
  };
  const removeCoupon = () => { setCouponCode(""); setCouponDiscount(0); setCouponApplied(false); };

  // ── Open Razorpay ─────────────────────────────────────────────────────────
  const openRazorpay = async (addressId, currentUser) => {
    const payload = { address_id: addressId, coupon_code: couponApplied ? couponCode : null };
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/orders/razorpay/create`, payload, { withCredentials: true });
      const options = {
        key:         data.key,
        amount:      data.amount,
        currency:    "INR",
        name:        "Rivora Jewels",
        description: "Order Payment",
        order_id:    data.razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/orders/razorpay/verify`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              },
              { withCredentials: true }
            );
            clearGuestCart();
            toast.success("Payment successful! Order placed.");
            navigate("/account");
          } catch {
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        prefill: { name: currentUser?.name || "", email: currentUser?.email || "" },
        theme:   { color: "#18181b" },
        modal:   { ondismiss: () => { toast.error("Payment cancelled."); setProcessing(false); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
      setProcessing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order. Please try again.");
      setProcessing(false);
    }
  };

  // ── Place Order — the main button handler ─────────────────────────────────
  const handlePlaceOrder = async () => {
    if (!user) {
      // Guest: require address form to be filled before showing sign-in
      if (!isAddressFormValid()) {
        toast.error("Please fill in your delivery address before placing the order.");
        // Auto-open the inline form if it's not open
        setShowAddressForm(true);
        return;
      }
      setShowAuthModal(true);
      return;
    }

    // Logged-in: need a selected address
    if (!selectedAddress) {
      toast.error("Please select or add a delivery address");
      return;
    }

    await openRazorpay(selectedAddress, user);
  };

  // ── After sign-in (guest path) ────────────────────────────────────────────
  const handleAuthSuccess = async (loggedInUser) => {
    setShowAuthModal(false);

    // 1. Merge guest cart items into backend
    const guests = getGuestCart();
    if (guests.length > 0) {
      try {
        for (const item of guests) {
          await axios.post(
            `${API}/cart`,
            { product_id: item.product_id, quantity: item.quantity },
            { withCredentials: true }
          );
        }
        clearGuestCart();
      } catch {
        toast.error("Could not sync your cart. Please try again.");
        return;
      }
    }

    // 2. Save the address the customer already filled in
    let addressId;
    try {
      const { data } = await axios.post(`${API}/addresses`, newAddress, { withCredentials: true });
      addressId = data.address_id;
      setAddresses([data]);
      setSelectedAddress(addressId);
    } catch {
      toast.error("Could not save your address. Please try again.");
      return;
    }

    // 3. Load the backend cart so cartItems updates to real data
    try {
      const cartRes = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(cartRes.data);
    } catch { /* non-fatal, order will still work */ }

    // 4. Open Razorpay directly — no re-render needed, pass user object explicitly
    await openRazorpay(addressId, loggedInUser);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent" />
      </div>
    );
  }

  if (cartItems.length === 0) {
    navigate("/cart");
    return null;
  }

  const finalTotal = calculateTotal() - couponDiscount;

  // ── Shared address input form JSX ─────────────────────────────────────────
  const AddressForm = ({ onSave, onCancel, saveLabel = "Save Address" }) => (
    <div className="border border-zinc-200 p-6 mt-4" data-testid="address-form">
      <h3 className="text-lg font-light cormorant mb-4">
        {user ? "New Address" : "Delivery Address"}
      </h3>
      <div className="space-y-4">
        <input type="text" placeholder="Full Name *"
          value={newAddress.full_name}
          onChange={e => setNewAddress({ ...newAddress, full_name: e.target.value })}
          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
          data-testid="address-full-name"
        />
        <input type="tel" placeholder="Phone Number *"
          value={newAddress.phone}
          onChange={e => setNewAddress({ ...newAddress, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
          data-testid="address-phone" maxLength={10}
        />
        <input type="text" placeholder="Address Line 1 *"
          value={newAddress.address_line1}
          onChange={e => setNewAddress({ ...newAddress, address_line1: e.target.value })}
          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
          data-testid="address-line1"
        />
        <input type="text" placeholder="Address Line 2 (Optional)"
          value={newAddress.address_line2}
          onChange={e => setNewAddress({ ...newAddress, address_line2: e.target.value })}
          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
          data-testid="address-line2"
        />
        <div className="grid grid-cols-2 gap-4">
          <input type="text" placeholder="City *"
            value={newAddress.city}
            onChange={e => setNewAddress({ ...newAddress, city: e.target.value })}
            className="border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
            data-testid="address-city"
          />
          <input type="text" placeholder="State *"
            value={newAddress.state}
            onChange={e => setNewAddress({ ...newAddress, state: e.target.value })}
            className="border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
            data-testid="address-state"
          />
        </div>
        <input type="text" placeholder="Pincode *"
          value={newAddress.pincode}
          onChange={e => setNewAddress({ ...newAddress, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
          className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
          data-testid="address-pincode" maxLength={6}
        />
        {onSave && (
          <div className="flex gap-3">
            <button onClick={onSave}
              className="bg-zinc-900 text-white hover:bg-zinc-800 px-6 py-2 text-sm uppercase tracking-wide transition-colors"
              data-testid="save-address-button"
            >
              {saveLabel}
            </button>
            {onCancel && (
              <button onClick={onCancel}
                className="border border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-6 py-2 text-sm uppercase tracking-wide transition-colors"
                data-testid="cancel-address-button"
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Sign-in modal — only shown when guest clicks Place Order */}
      {showAuthModal && (
        <AuthModal
          defaultMode="signin"
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuthModal(false)}
          onSaveCheckoutIntent={saveCheckoutIntent}
        />
      )}

      <div className="flex-1 px-6 md:px-12 max-w-7xl mx-auto py-12">
        <h1
          className="cormorant text-4xl sm:text-5xl font-light tracking-tight mb-12"
          data-testid="checkout-title"
        >
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* ── Left ─────────────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-8">

            {/* Delivery Address */}
            <div>
              <h2 className="text-xl font-light cormorant mb-4">Delivery Address</h2>

              {!user ? (
                /* Guest — show the address form directly, no sign-in required yet */
                <AddressForm onSave={null} />
              ) : (
                <>
                  {/* Saved addresses */}
                  {addresses.map(addr => (
                    <div
                      key={addr.address_id}
                      onClick={() => setSelectedAddress(addr.address_id)}
                      className={`p-4 border-2 cursor-pointer mb-3 ${
                        selectedAddress === addr.address_id
                          ? "border-zinc-900 bg-zinc-50"
                          : "border-zinc-200"
                      }`}
                      data-testid={`address-${addr.address_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{addr.full_name}</p>
                          <p className="text-sm text-zinc-600 mt-1">{addr.address_line1}</p>
                          {addr.address_line2 && (
                            <p className="text-sm text-zinc-600">{addr.address_line2}</p>
                          )}
                          <p className="text-sm text-zinc-600">
                            {addr.city}, {addr.state} – {addr.pincode}
                          </p>
                          <p className="text-sm text-zinc-600 mt-1">Phone: {addr.phone}</p>
                        </div>
                        <input
                          type="radio" name="address"
                          checked={selectedAddress === addr.address_id}
                          onChange={() => setSelectedAddress(addr.address_id)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ))}

                  {/* No addresses yet */}
                  {addresses.length === 0 && !showAddressForm && (
                    <div className="text-center py-6 border border-zinc-200 bg-zinc-50">
                      <p className="text-zinc-600 mb-4">No addresses saved</p>
                      <button onClick={() => setShowAddressForm(true)}
                        className="inline-flex items-center gap-2 text-sm text-zinc-900 hover:underline"
                        data-testid="add-new-address-button"
                      >
                        <Plus size={16} /> Add New Address
                      </button>
                    </div>
                  )}

                  {/* Add new address button */}
                  {addresses.length > 0 && !showAddressForm && (
                    <button onClick={() => setShowAddressForm(true)}
                      className="inline-flex items-center gap-2 text-sm text-zinc-900 hover:underline mt-2"
                      data-testid="show-address-form-button"
                    >
                      <Plus size={16} /> Add New Address
                    </button>
                  )}

                  {/* Address form for logged-in user adding a new address */}
                  {showAddressForm && (
                    <AddressForm
                      onSave={async () => {
                        if (!isAddressFormValid()) { toast.error("Please fill all required fields"); return; }
                        try {
                          await saveAddressToBackend();
                          setShowAddressForm(false);
                          toast.success("Address saved");
                        } catch { toast.error("Failed to save address"); }
                      }}
                      onCancel={() => { setShowAddressForm(false); setNewAddress(emptyAddr); }}
                    />
                  )}
                </>
              )}
            </div>

            {/* Payment Method */}
            <div>
              <h2 className="text-xl font-light cormorant mb-4">Payment Method</h2>
              <div
                onClick={() => setPaymentMethod("online")}
                className={`p-4 border-2 cursor-pointer ${
                  paymentMethod === "online" ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"
                }`}
                data-testid="payment-online"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Online Payment</p>
                    <p className="text-sm text-zinc-600">Pay securely via Razorpay (UPI, Cards, Net Banking)</p>
                  </div>
                  <input type="radio" name="payment" checked={paymentMethod === "online"} onChange={() => setPaymentMethod("online")} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: order summary ──────────────────────────────────── */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-zinc-200 p-6">
              <h2 className="text-xl font-light cormorant mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                {cartItems.map(item => (
                  <div key={item.product_id} className="flex gap-3">
                    <img
                      src={resolveImageUrl(item.product?.image_url)}
                      alt={item.product?.name}
                      className="w-16 h-16 object-cover bg-zinc-100"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-light">{item.product?.name}</p>
                      <p className="text-xs text-zinc-500">Qty: {item.quantity}</p>
                      <p className="text-sm font-medium mt-1">
                        ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-6 pt-6 border-t border-zinc-200">
                {/* Coupon */}
                <div className="mb-4">
                  {!couponApplied ? (
                    <>
                      <div className="flex gap-2">
                        <input type="text" value={couponCode}
                          onChange={e => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="Coupon code"
                          className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900"
                          data-testid="coupon-input"
                        />
                        <button onClick={applyCoupon}
                          className="bg-zinc-900 text-white px-4 py-2 text-xs uppercase tracking-wide"
                          data-testid="apply-coupon-button"
                        >Apply</button>
                      </div>
                      {availableCoupons.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          <p className="text-xs text-zinc-400 uppercase tracking-wide">Available offers</p>
                          {availableCoupons.map(c => (
                            <button key={c.code} onClick={() => setCouponCode(c.code)}
                              className={`w-full text-left flex items-center justify-between border rounded px-3 py-2 transition-colors hover:border-zinc-900 hover:bg-zinc-50 ${couponCode === c.code ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`}
                            >
                              <div>
                                <span className="font-mono text-sm font-semibold text-zinc-900">{c.code}</span>
                                <p className="text-xs text-zinc-500 mt-0.5">
                                  {c.discount_type === "percentage" ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                                  {c.min_order_value > 0 && ` · Min ₹${c.min_order_value}`}
                                </p>
                              </div>
                              <span className="text-[10px] text-zinc-400 uppercase tracking-wide">Tap to apply</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex justify-between items-center bg-green-50 border border-green-200 px-3 py-2 rounded">
                      <span className="text-sm text-green-800 font-medium">{couponCode} applied!</span>
                      <button onClick={removeCoupon} className="text-xs text-red-600 hover:underline" data-testid="remove-coupon-button">Remove</button>
                    </div>
                  )}
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600">Subtotal</span>
                  <span>₹{calculateTotal().toLocaleString()}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Coupon Discount</span>
                    <span>-₹{couponDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-600">Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="border-t border-zinc-200 pt-3 flex justify-between">
                  <span className="font-medium">Total</span>
                  <span className="text-xl font-medium" data-testid="checkout-total">
                    ₹{finalTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={processing || (user && !selectedAddress)}
                className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-8 py-4 uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
                data-testid="place-order-button"
              >
                {processing ? "Processing..." : user ? "Place Order" : "Sign In & Place Order"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
